/**
 * Get Login Audit History Page
 *
 * Displays detailed login audit history for a specific organization.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, LogIn, LogOut, Monitor, Laptop } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Button,
  Card,
  CardHeader,
  CardBody,
} from '@/components/common';
import { organizationService } from '@/api/services';
import type { LoginAuditHistory, ListRequest } from '@/api/types';
import { formatDateTime } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';

// ============================================
// Page Component
// ============================================

export function GetLoginAuditHistoryPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { orgId } = useParams<{ orgId: string }>();

  // State
  const [data, setData] = useState<LoginAuditHistory[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch data
  const fetchHistoryData = useCallback(
    async (params: { pageIndex: number; pageSize: number; sorting?: SortingState; search?: string }) => {
      if (!orgId) {
        toast.error('Organization ID is missing');
        return;
      }

      setIsLoading(true);
      try {
        const request: ListRequest = {
          paging: {
            page: params.pageIndex + 1,
            size: params.pageSize,
          },
          filtering: params.search ? { userName: params.search } : {},
          sorting:
            params.sorting && params.sorting.length > 0
              ? { [params.sorting[0].id]: params.sorting[0].desc ? 'desc' : 'asc' }
              : {},
        };

        const result = await organizationService.getLoginAuditHistory(orgId, request);
        setData(result.list);
        setTotalRows(result.filteredRecords);
      } catch (error) {
        console.error('Failed to fetch login audit history:', error);
        toast.error('Failed to load login audit history');
      } finally {
        setIsLoading(false);
      }
    },
    [orgId, toast]
  );

  // Debounce search term
  useEffect(() => {
    // If search is cleared, update immediately
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial load
  useEffect(() => {
    fetchHistoryData({ ...pagination, sorting, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedSearchTerm]);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    []
  );

  // Navigate back to login audit page
  const handleBack = useCallback(() => {
    navigate('/audit');
  }, [navigate]);

  // Column definitions
  const columns = useMemo<ColumnDef<LoginAuditHistory>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <User className="h-4 w-4 text-primary" />
            </div>
            <span className="font-medium">{row.original.username}</span>
          </div>
        ),
      },
      {
        accessorKey: 'loginTime',
        header: 'Login Time',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <LogIn className="h-3 w-3 text-foreground-muted dark:text-foreground-dark-muted" />
            <span className="text-sm">
              {row.original.loginTime ? formatDateTime(row.original.loginTime) : 'N/A'}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'logoutTime',
        header: 'Logout Time',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <LogOut className="h-3 w-3 text-foreground-muted dark:text-foreground-dark-muted" />
            <span className="text-sm">
              {row.original.logoutTime ? formatDateTime(row.original.logoutTime) : 'N/A'}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'deviceType',
        header: 'Device Type',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Laptop className="h-3 w-3 text-foreground-muted dark:text-foreground-dark-muted" />
            <span className="text-xs">{row.original.deviceType || 'N/A'}</span>
          </div>
        ),
      },
      {
        accessorKey: 'ipAddress',
        header: 'IP Address',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Monitor className="h-3 w-3 text-foreground-muted dark:text-foreground-dark-muted" />
            <span className="font-mono text-xs">{row.original.ipAddress || 'N/A'}</span>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Login Audit History
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Detailed login history for organization: {orgId}
          </p>
        </div>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="User Login Activity"
          description="View detailed login and logout activity for all users in this organization"
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchable
            searchPlaceholder="Search by username..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            emptyMessage="No login history found"
            emptyDescription="There is no login activity recorded for this organization yet."
            className="border-0"
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default GetLoginAuditHistoryPage;
